import React from 'react';
import { DeleteOutlined } from '@ant-design/icons';
import {
	Alert,
	Flex,
	Popover,
	Space,
	Tooltip,
	TooltipProps,
} from 'antd';
import { Moment } from 'moment';
import classNames from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Icon } from '@fortawesome/fontawesome-svg-core';
import { faWrench } from '@fortawesome/pro-solid-svg-icons';
import {
	PortActionTypes,
	EstimatorModeTypes,
	TimeZoneFormat,
	TimeZoneMode,
	DATE,
	TIME,
	DATE_AND_TIME,
	ConsumptionSetAttachedTo,
	LaytimeIntervals,
	SeaPassageToggles,
} from '@shared/utils/constants';
import { convertDaysToInterval, convertIntervalToDays } from '@shared/utils/laytime';
import { PortRotationEntryGeneratorTypes } from '@shared/utils/port-rotation-entry-generator-types';
import { standardSort } from '@shared/utils/standardSort';
import { Values } from '@shared/utils/objectEnums';
import { formatAndRoundNumber } from '@shared/utils/formatNumber';
import {
	fromISOTimezone,
	timeZoneOffsetLabel,
	TimeZoneValue,
	toISOTimezone,
} from '@shared/utils/timezones';
import { toMoment } from '@shared/utils/date';
import { round } from '@shared/utils/math';
import { capitalize } from '@shared/utils/string';
import type { Port } from '@api/utils/ports';
import { type ConsumptionSetProps } from '@api/models/consumption-set';
import { CargoDetails } from '@api/features/cargos/getCargoDetails';
import { SeaPassageSettings } from '@api/utils/sequelize/calculateVoyageItineraryDetails/helpers/itineraryConstants';
import { FuelTypeValue } from '@api/utils/getVesselFuelGrades';
import Button from '@client/components/Button';
import { EditableColumns } from '@client/components/EditableTableRedux/EditableCellTableRedux';
import Select from '@client/components/Select';
import NumericInput from '@client/components/NumericInput';
import DatePicker from '@client/components/DatePicker';
import { formatDate } from '@client/utils/formatDate';
import RequiredArrivalPopover from '@client/components/RequiredArrivalPopover/RequiredArrivalPopover';
import { renderConsumptionSetOptions } from '@client/components/ConsumptionSet/renderConsumptionSetOptions';
import ConfigContent from '../ConfigContent';
import SeaPassageTogglesControl from '../SeaPassageTogglesControl';
import styles from '../styles/shared.module.css';
import {
	LocalPortRotation,
	PartialPortRotationEntry,
} from '../context/hooks/usePortRotationHandlers';
import SeaPassageSwitch from './SeaPassageSwitch';

type PortRotationColumn = EditableColumns<LocalPortRotation>[number];

type GeneratedBy = Values<typeof PortRotationEntryGeneratorTypes> | null;

type ActionTypeLabels = Record<string, string>;

export const getNegativeRobEntryIds = (
	portRotation: LocalPortRotation[],
	vesselFuelGrades: Array<FuelTypeValue>,
) => {
	const ids = new Set<number>();

	const hasNegativeRob = (entry: LocalPortRotation, type: 'arrival' | 'departure') => (
		vesselFuelGrades.some((fuelGrade) => (
			Number((entry as Record<string, any>)[`${fuelGrade}-${type}`]) < 0
		))
	);

	portRotation.forEach((entry, index) => {
		if (hasNegativeRob(entry, 'arrival')) {
			ids.add(entry.id);

			const previousEntry = portRotation[index - 1];

			if (previousEntry != null) {
				ids.add(previousEntry.id);
			}
		}

		if (hasNegativeRob(entry, 'departure')) {
			ids.add(entry.id);
		}
	});

	return ids;
};

export const renderPortNameWithRobWarning = (label: React.ReactNode, hasNegativeRob: boolean) => {
	if (!hasNegativeRob) {
		return label;
	}

	return (
		<Popover
			trigger="click"
			content={(
				<Alert
					message="Bunkers go below zero. You may need to stem bunkers"
					type="warning"
					showIcon
				/>
			)}
		>
			<span className={styles.negativeRobPort}>
				{label}
			</span>
		</Popover>
	);
};

export const isSpecialType = (
	generatedBy: GeneratedBy,
	onlyBallast = true,
) => {
	if (generatedBy == null) {
		return false;
	}

	if (onlyBallast) {
		return generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG;
	}

	if (
		generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG ||
		generatedBy === PortRotationEntryGeneratorTypes.DELIVERY_PORT ||
		generatedBy === PortRotationEntryGeneratorTypes.REDELIVERY_PORT
	) {
		return true;
	}

	return false;
};

export const isBallastLeg = (generatedBy: GeneratedBy) => (
	generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG
);

export const renderWrappingTime = (m: Moment, timeFormat: TimeZoneFormat) => {
	const datePart = formatDate(m, DATE, false, timeFormat);
	const timePart = formatDate(m, TIME, true, timeFormat);

	return (
		<div>
			{datePart}
			<br />
			{timePart}
		</div>
	);
};

export const getDeleteColumn = ({
	onDelete,
	disabled,
}: {
	onDelete: (entry: LocalPortRotation) => void;
	disabled?: boolean;
}): PortRotationColumn => ({
	title: '',
	editable: false,
	render: (portRotationEntry: LocalPortRotation) => (
		<Button
			type="link"
			danger
			size="small"
			confirmTitle="Are you sure you want to delete this entry?"
			onClick={() => onDelete(portRotationEntry)}
			disabledTooltip="Auto-generated entries cannot be deleted directly"
			disabled={portRotationEntry.generatedBy != null || disabled}
			tooltipProps={{
				placement: 'topRight',
			} as TooltipProps}
			icon={(
				<DeleteOutlined
					style={{
						fontSize: '16px',
						color: portRotationEntry.generatedBy != null ? 'hsl(0, 100%, 90%)' : 'red',
					}}
				/>
			)}
		/>
	),
	width: '30px',
});

export const getConfigColumn = ({
	onConfigSave,
	onSeaPassageTogglesSave,
	portRotation,
	disabled,
}: {
	onConfigSave: (id: number, routeSettings: SeaPassageSettings) => void;
	onSeaPassageTogglesSave?: (toggles: Partial<SeaPassageToggles>) => void;
	portRotation?: LocalPortRotation[];
	disabled?: boolean;
}): PortRotationColumn => ({
	title: onSeaPassageTogglesSave != null && portRotation != null ? (
		<SeaPassageTogglesControl
			portRotation={portRotation}
			onSave={onSeaPassageTogglesSave}
			disabled={disabled}
		/>
	) : '',
	editable: false,
	className: styles.cellNoPadding,
	render: (portRotationEntry: PartialPortRotationEntry) => (
		<ConfigContent
			portRotationId={portRotationEntry.id}
			handleConfigUpdate={(id, settings) => id != null && onConfigSave(id, settings)}
			routeOptions={portRotationEntry.routeOptions}
			disabled={
				portRotationEntry.type === PortActionTypes.CANAL_TRANSIT ||
				portRotationEntry.type === PortActionTypes.BALLAST_LEG ||
				disabled
			}
		/>
	),
	width: '40px',
});

export const getTypeColumn = ({
	disabled,
	actionTypes,
	actionTypeLabels,
	shortActionTypeLabels,
}: {
	disabled?: boolean;
	actionTypes: ActionTypeLabels;
	actionTypeLabels: ActionTypeLabels;
	shortActionTypeLabels: ActionTypeLabels;
}): PortRotationColumn => ({
	title: 'Type',
	dataIndex: 'type',
	type: 'select',
	renderValue: (p) => shortActionTypeLabels[p.value as string] ?? p.value,
	editable: (r: PartialPortRotationEntry) => r.generatedBy == null && !disabled,
	className: (r: PartialPortRotationEntry) => (
		r.generatedBy != null ?
			styles.calculated :
			undefined
	),
	inputProps: {
		showSearch: true,
		className: styles.typeSelector,
		options: Object.values(actionTypes)
			.map((type) => ({
				label: actionTypeLabels[type],
				value: type,
			}))
			.sort(standardSort('label')),
		popupMatchSelectWidth: false,
		popupClassName: styles.typeSelector,
		labelRender: (p) => shortActionTypeLabels[p.value as string],
	},
	width: 30,
});

export const getPortColumn = ({
	ports,
	disabled,
	isGeneratedPortEditable,
	renderValue,
	inputPropsWithRow,
}: {
	ports: Port[];
	disabled?: boolean;
	isGeneratedPortEditable: (generatedBy: GeneratedBy) => boolean;
	renderValue?: (p: any) => React.ReactNode;
	inputPropsWithRow?: (row: PartialPortRotationEntry) => {};
}): PortRotationColumn => ({
	title: 'Port',
	dataIndex: 'port',
	type: 'select',
	editable: (r: PartialPortRotationEntry) => ((
		r.generatedBy == null ||
		isGeneratedPortEditable(r.generatedBy)
	) && !disabled),
	className: (r: PartialPortRotationEntry) => {
		if (
			r.generatedBy != null &&
			!isGeneratedPortEditable(r.generatedBy)
		) {
			return styles.calculated;
		}

		return undefined;
	},
	inputProps: {
		placeholder: 'Select port',
		className: styles.slimInput,
		showAction: ['focus'],
		options: ports.map((port) => ({
			label: port.name?.toUpperCase(),
			value: port.id,
		})).sort(standardSort('label')),
		showSearch: true,
		dropdownStyle: {
			minWidth: '250px',
		},
	},
	inputPropsWithRow,
	transformData: {
		in: (port: Port | null) => port?.id ?? null,
		out: (portId: number | null) => (
			ports.find((p) => p.id === portId) ?? null
		),
	},
	renderValue,
});

export const getArrivalColumn = ({
	timeZoneFormat,
	cargos,
	portRotation,
	disabled,
	consumptionSets,
	requiredArrivalPopoverOpen,
	setRequiredArrivalPopoverOpen,
	onPortRotationRowChange,
	refreshVessel,
}: {
	timeZoneFormat: TimeZoneFormat;
	cargos: CargoDetails[];
	portRotation: LocalPortRotation[];
	disabled?: boolean;
	consumptionSets: Array<ConsumptionSetProps>;
	requiredArrivalPopoverOpen: number | null;
	setRequiredArrivalPopoverOpen: React.Dispatch<React.SetStateAction<number | null>>;
	onPortRotationRowChange: (id: number, attributes: any) => void;
	refreshVessel: () => void;
}): PortRotationColumn => {
	const availableSpeeds = consumptionSets
		.map((set) => set.ladenSpeed)
		.filter((speed) => Number.isFinite(speed) && speed > 0);

	const minSpeed = availableSpeeds.length > 0 ? Math.min(...availableSpeeds) : null;
	const maxSpeed = availableSpeeds.length > 0 ? Math.max(...availableSpeeds) : null;
	const vesselId = consumptionSets.at(0)?.vesselId ?? null;

	return {
		title: 'Arrival',
		dataIndex: 'arrivalDate',
		type: 'date',
		editable: false,
		className: (row) => {
			if (row.generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG) {
				return styles.disabled;
			}

			return styles.calculated;
		},
		transformData: {
			in: (
				iso: string | null,
				row: LocalPortRotation,
			) => ({
				iso: fromISOTimezone(iso, timeZoneFormat, row?.port?.timezone, TimeZoneMode.Instant),
				tz: row?.port?.timezone ?? null,
			}),
			out: (
				val: Moment | null,
				row?: LocalPortRotation,
			) => toISOTimezone(val, timeZoneFormat, row?.port?.timezone, TimeZoneMode.WallTime),
		},
		renderValue: (p: any) => {
			if (p.value?.iso == null) {
				return null;
			}

			const relevantCargo = cargos.find((c) => c.id === p.row.cargoId);
			const relevantCargoPort = relevantCargo?.CargoPorts?.find((cp) => cp.id === p.row.cargoPortId);

			let late = false;
			const isLoading = p.row.type === 'loading';
			const lastLaycan = relevantCargoPort?.laycanTo;

			if (lastLaycan != null && isLoading) {
				late = toMoment(p.value.iso).isAfter(toMoment(lastLaycan));
			}

			if (late && p.value.iso && lastLaycan != null) {
				return (
					<Tooltip
						title={
							'The vessel is arriving after the cancelling date of the cargo ' +
							`(${formatDate(lastLaycan, DATE_AND_TIME)})`
						}
					>
						<span style={{ color: 'red' }}>
							{renderWrappingTime(p.value.iso, timeZoneFormat)}
						</span>
					</Tooltip>
				);
			}

			if (p.value.iso) {
				const row = p.row as LocalPortRotation;
				const previousRow = row.previousEntryId == null ?
					null :
					portRotation.find((entry) => entry.id === row.previousEntryId) ?? null;

				const isTargetDisabled =
					disabled ||
					p.row.generatedBy === PortRotationEntryGeneratorTypes.BALLAST_LEG ||
					previousRow == null ||
					previousRow.departureDate == null ||
					previousRow.port == null ||
					row.port == null ||
					vesselId == null ||
					minSpeed == null ||
					maxSpeed == null;

				return (
					<>
						{renderWrappingTime(p.value.iso, timeZoneFormat)}
						<Space size="small">
							<RequiredArrivalPopover
								open={requiredArrivalPopoverOpen === p.row.id}
								setOpen={(nextOpen) => {
									setRequiredArrivalPopoverOpen(nextOpen ? p.row.id : null);
								}}
								disabled={isTargetDisabled}
								timeZoneFormat={timeZoneFormat}
								currentArrivalDate={p.value?.iso ?? null}
								previousDepartureDate={previousRow?.departureDate ?? null}
								fromPort={previousRow?.port as Port}
								toPort={row.port as Port}
								vesselId={vesselId ?? 0}
								estimateId={row.estimateId ?? 0}
								currentConsumptionSetId={previousRow?.consumptionSetId}
								attachedTo={ConsumptionSetAttachedTo.ESTIMATE}
								options={{
									...row.routeOptions,
									seaMargin: row.seaMargin ?? 0,
									useMedEca: true,
								}}
								refreshAfterPreview={refreshVessel}
								onApply={({ consumptionSetId }) => {
									if (previousRow == null) {
										return;
									}

									const attributes: any = {
										consumptionSetId,
									};

									if (previousRow.preRequiredArrivalConsumptionSetId == null) {
										attributes.preRequiredArrivalConsumptionSetId = previousRow.consumptionSetId;
									}

									onPortRotationRowChange(previousRow.id, attributes);
									refreshVessel();
								}}
							/>
						</Space>
					</>
				);
			}

			return null;
		},
		inputProps: {
			allowClear: false,
		},
		width: 135,
	};
};

export const getDepartureColumn = ({
	timeZoneFormat,
	disabled,
	localTimeWidth,
}: {
	timeZoneFormat: TimeZoneFormat;
	disabled?: boolean;
	localTimeWidth: number;
}): PortRotationColumn => ({
	title: 'Departure',
	dataIndex: 'departureDate',
	type: 'date',
	editable: (row) => isBallastLeg(row.generatedBy) && !disabled,
	transformData: {
		in: (iso: string | null, row: LocalPortRotation) => ({
			iso: fromISOTimezone(iso, timeZoneFormat, row?.port?.timezone, TimeZoneMode.Instant),
			tz: row?.port?.timezone ?? null,
		}),
		out: (
			val: Moment | null,
			row?: LocalPortRotation,
		) => toISOTimezone(val, timeZoneFormat, row?.port?.timezone, TimeZoneMode.WallTime),
	},
	renderValue: (p: any) => (p.value?.iso ? (
		<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
			{renderWrappingTime(p.value.iso, timeZoneFormat)}
		</div>
	) : null),
	renderInput: ({ value, onChange }) => {
		const { iso, tz } = (value ?? {}) as TimeZoneValue;

		return (
			<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
				<DatePicker
					time
					showTimezone={false}
					disableTimezone
					value={iso}
					onChange={(picked) => onChange(
						!picked || Array.isArray(picked) ?
							null :
							(picked as Moment),
					)}
				/>
				<span className={styles.timezoneLabel}>
					{timeZoneOffsetLabel(iso, tz, timeZoneFormat)}
				</span>
			</div>
		);
	},
	inputProps: {
		allowClear: false,
		time: true,
		className: styles.slimInput,
		showTimezone: false,
	},
	className: (row) => (!isBallastLeg(row.generatedBy) ?
		styles.calculated :
		undefined),
	width: timeZoneFormat === TimeZoneFormat.LocalTime ? localTimeWidth : 260,
});

export const getDistanceColumn = ({
	renderDistance,
}: {
	renderDistance: (value: any) => React.ReactNode;
}): PortRotationColumn => ({
	title: 'Distance',
	children: [
		{
			title: 'Normal',
			dataIndex: 'normalDistance',
			type: 'number',
			editable: false,
			className: (row) => (
				isBallastLeg(row.generatedBy) ?
					styles.disabled :
					styles.calculatedNumber
			),
			renderValue: (p) => (p.value != null ? renderDistance(p.value) : null),
			width: 40,
		},
		{
			title: 'ECA',
			dataIndex: 'ecaDistance',
			type: 'number',
			editable: false,
			className: (row) => (
				isBallastLeg(row.generatedBy) ?
					styles.disabled :
					styles.calculatedNumber
			),
			renderValue: (p) => (p.value != null ? renderDistance(p.value) : null),
			width: 40,
		},
	],
});

export const getSeaMarginColumn = ({
	disabled,
}: {
	disabled?: boolean;
}): PortRotationColumn => ({
	title: 'Marg.',
	dataIndex: 'seaMargin',
	inputProps: {
		placeholder: 'Margin',
		className: styles.slimInput,
		addonAfter: '%',
		size: 'small',
	},
	type: 'number',
	editable: (row) => (row.seaDays != null && row.seaDays !== 0) && !disabled,
	className: (row) => (
		row.seaDays != null && row.seaDays !== 0 ?
			undefined :
			styles.disabled
	),
	width: 50,
});

export const getSpeedColumn = ({
	portRotation,
	consumptionSets,
	defaultConsumptionSetId,
	disabled,
	hasCargoRelet,
	speedPopoverOpen,
	setSpeedPopoverOpen,
	onPortRotationChange,
	onPortRotationRowChange,
}: {
	portRotation: LocalPortRotation[];
	consumptionSets: Array<ConsumptionSetProps>;
	defaultConsumptionSetId: number | undefined;
	disabled?: boolean;
	hasCargoRelet: boolean;
	speedPopoverOpen: number | null;
	setSpeedPopoverOpen: React.Dispatch<React.SetStateAction<number | null>>;
	onPortRotationChange: (entries: LocalPortRotation[], immediate?: boolean) => void;
	onPortRotationRowChange: (id: number, attributes: any) => void;
}): PortRotationColumn => {
	const getPreviousRow = (row: LocalPortRotation) => {
		const currentIndex = portRotation.findIndex((entry) => entry.id === row.id);

		return currentIndex > 0 ? portRotation[currentIndex - 1] : null;
	};

	return {
		title: (
			<Flex
				gap={5}
				align="center"
				justify="center"
			>
				<p style={{ margin: 0, padding: 0 }}>Speed</p>
				<Popover
					title="Choose consumption set"
					trigger="click"
					open={speedPopoverOpen === -1 && !hasCargoRelet}
					onOpenChange={(open) => (
						open ?
							setSpeedPopoverOpen(-1) :
							setSpeedPopoverOpen(null)
					)}
					content={(
						<Flex
							gap={10}
							vertical
						>
							<Alert
								style={{ width: 300 }}
								message="The chosen consumption profile will apply to all sea passages in the port rotation."
								type="info"
								showIcon
							/>
							<Select
								disabled={disabled || hasCargoRelet}
								fullWidth
								placeholder="Select consumption profile"
								value={defaultConsumptionSetId}
								optionFilterProp="title"
								options={renderConsumptionSetOptions(consumptionSets)}
								onSelect={(v) => {
									const newEntries = portRotation.map((p) => ({
										...p,
										consumptionSetId: v,
									}));
									onPortRotationChange(newEntries, true);
									setSpeedPopoverOpen(null);
								}}
							/>
						</Flex>
					)}
				>
					<Button
						type="link"
						className={styles.speedProfileBtn}
						icon={(<FontAwesomeIcon size="sm" icon={faWrench as Icon} />)}
					/>
				</Popover>
			</Flex>
		),
		type: 'number',
		inputProps: {
			placeholder: 'Speed',
		},
		editable: false,
		width: 75,
		className: (row) => (getPreviousRow(row) == null ? styles.disabled : styles.calculated),
		render: (row: LocalPortRotation) => {
			if (row == null) {
				return null;
			}

			const previousRow = getPreviousRow(row);

			if (previousRow == null) {
				return null;
			}

			const speed = Number(
				hasCargoRelet ?
					consumptionSets.at(0)?.ladenSpeed :
					previousRow.speed ?? 0
			);

			const displaySpeed =
				Number.isInteger(speed) ?
					round(speed, 0) :
					Number(round(speed)).toString();

			return (
				<Flex
					align="center"
					gap={6}
					className={styles.speedProfileContainer}
				>
					<span style={{ whiteSpace: 'nowrap' }}>
						{`${displaySpeed} kts`}
					</span>
					<Popover
						title="Choose consumption set"
						trigger="click"
						open={speedPopoverOpen === row.id && !hasCargoRelet}
						onOpenChange={(open) => (
							open ?
								setSpeedPopoverOpen(row.id) :
								setSpeedPopoverOpen(null)
						)}
						content={(
							<Flex gap={10} vertical>
								<Alert
									style={{ width: 300 }}
									message={`
									The chosen consumption profile will be used to calculate the speed
									for the sea passage ending at this row.
								`}
									type="info"
									showIcon
								/>
								<Select
									disabled={disabled || hasCargoRelet}
									fullWidth
									value={previousRow.consumptionSetId}
									optionFilterProp="title"
									options={renderConsumptionSetOptions(consumptionSets)}
									onSelect={(v) => {
										onPortRotationRowChange(previousRow.id, {
											consumptionSetId: Number(v),
										});
										setSpeedPopoverOpen(null);
									}}
								/>
							</Flex>
						)}
					>
						<Button
							type="link"
							className={styles.speedProfileBtn}
							icon={(<FontAwesomeIcon size="sm" icon={faWrench as Icon} />)}
						/>
					</Popover>
				</Flex>
			);
		},
	};
};

export const getSeaDaysColumn = ({
	title,
	renderDays,
}: {
	title: string;
	renderDays: (value: any) => React.ReactNode;
}): PortRotationColumn => ({
	title,
	dataIndex: 'seaDays',
	type: 'number',
	width: 50,
	align: 'right',
	editable: false,
	className: (row) => (
		isBallastLeg(row.generatedBy) ?
			styles.disabled :
			styles.calculatedNumber
	),
	renderValue: (p) => (p.value != null ? renderDays(p.value) : null),
});

export const getWorkingDaysColumn = ({
	disabled,
	hidden,
	inputProps,
	testId,
	renderDays,
}: {
	disabled?: boolean;
	hidden?: boolean;
	inputProps: Record<string, any>;
	testId?: string;
	renderDays: (value: any) => React.ReactNode;
}): PortRotationColumn => ({
	title: 'Working',
	dataIndex: 'workingDays',
	hidden,
	type: 'number',
	'data-testid': testId,
	inputProps,
	width: 45,
	align: 'right',
	editable: (row) => (
		!isBallastLeg(row.generatedBy) &&
		row.generatedBy !== PortRotationEntryGeneratorTypes.CARGO &&
		!disabled
	),
	className: (row) => {
		if (isBallastLeg(row.generatedBy)) {
			return styles.disabled;
		}

		if (row.generatedBy === PortRotationEntryGeneratorTypes.CARGO) {
			return styles.calculatedNumber;
		}

		return undefined;
	},
	renderValue: (p) => (p.value != null ? renderDays(p.value) : '0'),
});

const idleTurnTimeTransform = {
	in: (days: number | null, row: PartialPortRotationEntry) => (
		days == null ? days : round(convertDaysToInterval(days, row.idleTurnUnit), 6)
	),
	out: (value: number | null, row: PartialPortRotationEntry) => (
		value == null ? value : convertIntervalToDays(value, row.idleTurnUnit)
	),
};

const formatIdleTurnUnit = (unit: string | number | null | undefined) => (
	unit === LaytimeIntervals.HOUR ? 'H' : 'D'
);

export const getIdleTurnUnitColumn = ({
	disabled,
}: {
	disabled?: boolean;
}): PortRotationColumn => ({
	title: 'Unit',
	dataIndex: 'idleTurnUnit',
	type: 'select',
	width: 45,
	editable: (row) => !isBallastLeg(row.generatedBy) && !disabled,
	className: (row) => classNames(
		styles.idleTurnGroupEnd,
		isBallastLeg(row.generatedBy) && styles.disabled,
	),
	onHeaderCell: () => ({ className: classNames(styles.idleTurnGroupHeader, styles.idleTurnGroupEnd) }),
	inputProps: {
		className: styles.slimInput,
		'data-testid': 'idle-turn-unit',
		options: Object.values(LaytimeIntervals).map((interval) => ({
			label: capitalize(interval),
			value: interval,
		})),
		popupMatchSelectWidth: false,
		labelRender: (p) => formatIdleTurnUnit(p.value),
	},
	renderValue: (p) => formatIdleTurnUnit(p.value),
});

export const getIdleDaysColumn = ({
	disabled,
	hidden,
	inputProps,
	waitForLaycan,
	withIdleTurnUnit,
	renderDays,
}: {
	disabled?: boolean;
	hidden?: boolean;
	inputProps: Record<string, any>;
	waitForLaycan?: boolean;
	withIdleTurnUnit?: boolean;
	renderDays: (value: any) => React.ReactNode;
}): PortRotationColumn => ({
	title: 'Idle',
	dataIndex: 'idleDays',
	hidden,
	type: 'number',
	'data-testid': 'idle-days',
	width: 45,
	inputProps,
	align: 'right',
	transformData: withIdleTurnUnit ? idleTurnTimeTransform : undefined,
	onHeaderCell: withIdleTurnUnit ?
		() => ({ className: classNames(styles.idleTurnGroupHeader, styles.idleTurnGroupStart) }) :
		undefined,
	editable: (row) => {
		if (isBallastLeg(row.generatedBy)) {
			return false;
		}

		if (disabled) {
			return false;
		}

		if (waitForLaycan && row.type === PortActionTypes.LOADING) {
			return false;
		}

		return true;
	},
	className: (row) => {
		const groupClassName = withIdleTurnUnit ? styles.idleTurnGroupStart : undefined;

		if (isBallastLeg(row.generatedBy)) {
			return classNames(groupClassName, styles.disabled);
		}

		if (waitForLaycan && row.type === PortActionTypes.LOADING) {
			return classNames(groupClassName, styles.calculatedNumber);
		}

		return groupClassName;
	},
	renderValue: (p) => (p.value != null ? renderDays(p.value) : 'N/A'),
});

export const getTurnDaysColumn = ({
	disabled,
	hidden,
	inputProps,
	withIdleTurnUnit,
	renderDays,
}: {
	disabled?: boolean;
	hidden?: boolean;
	inputProps: Record<string, any>;
	withIdleTurnUnit?: boolean;
	renderDays: (value: any) => React.ReactNode;
}): PortRotationColumn => ({
	title: 'Turn',
	dataIndex: 'turnDays',
	hidden,
	type: 'number',
	inputProps,
	width: 45,
	align: 'right',
	transformData: withIdleTurnUnit ? idleTurnTimeTransform : undefined,
	onHeaderCell: withIdleTurnUnit ? () => ({ className: styles.idleTurnGroupHeader }) : undefined,
	editable: (row) => !isBallastLeg(row.generatedBy) && !disabled,
	className: (row) => (
		isBallastLeg(row.generatedBy) ?
			styles.disabled : undefined
	),
	renderValue: (p) => (p.value != null ? renderDays(p.value) : '0'),
});

const SEA_PASSAGE_SWITCH_COLUMNS: Array<{
	title: string;
	field: keyof SeaPassageSettings;
	width: number;
}> = [
	{ title: 'Kiel', field: 'allowKielTransit', width: 40 },
	{ title: 'Suez', field: 'allowSuezTransit', width: 40 },
	{ title: 'Panama', field: 'allowPanamaTransit', width: 40 },
	{ title: 'Great Belt (Storebælt)', field: 'allowGreatBeltTransit', width: 40 },
	{ title: 'Danish Sound (Øresund)', field: 'allowDanishSoundTransit', width: 40 },
	{ title: 'Torres Strait', field: 'allowTorresStraitTransit', width: 40 },
	{ title: 'Avoid HRA', field: 'avoidHRA', width: 60 },
	{ title: 'Avoid ECA', field: 'avoidSeca', width: 60 },
	{ title: 'Ice Areas', field: 'allowIceAreas', width: 60 },
];

export const getRouteOptionColumns = ({
	mode,
	disabled,
	onConfigUpdate,
}: {
	mode: EstimatorModeTypes;
	disabled?: boolean;
	onConfigUpdate: (id: number, routeSettings: SeaPassageSettings) => void;
}): EditableColumns<LocalPortRotation> => [
	{
		title: 'Draft override',
		hidden: mode !== EstimatorModeTypes.MAP,
		type: 'number',
		width: 50,
		editable: !disabled,
		render: (portRotationEntry: PartialPortRotationEntry) => {
			const value = portRotationEntry.routeOptions?.overrideDraft;

			return (
				<NumericInput
					disabled={disabled}
					value={value}
					placeholder="Draft (m)"
					onChange={(newValue) => (
						portRotationEntry?.id != null ? onConfigUpdate(portRotationEntry.id, {
							...portRotationEntry.routeOptions,
							overrideDraft: newValue == null ? undefined : newValue,
						}) : undefined)}
				/>
			);
		},
	},
	...SEA_PASSAGE_SWITCH_COLUMNS.map(({ title, field, width }) => ({
		title,
		hidden: mode !== EstimatorModeTypes.MAP,
		type: 'switch' as const,
		width,
		editable: !disabled,
		render: (portRotationEntry: PartialPortRotationEntry) => (
			<SeaPassageSwitch disabled={disabled} portRotationEntry={portRotationEntry} field={field} />
		),
	})),
];

export const formatDays = (value: any) => formatAndRoundNumber(value, 2);
